/**
 * MoveHistory Component for Checkers
 *
 * Displays checkers moves in board notation (1-32 numbering) in a scrollable panel.
 * Moves are shown in chronological order with move numbers.
 *
 * Requirements: 16.1, 16.2, 16.3, 16.4, 16.5
 */

import React, { useRef, useEffect } from 'react';
import { CheckersMove, PlayerColor } from '../types';
import './MoveHistory.css';

export interface CheckersMoveHistoryProps {
  moves: CheckersMove[];
  currentMoveIndex?: number;
  onMoveClick?: (moveIndex: number) => void;
  className?: string;
}

/**
 * MoveHistory component displays the game's move history using board notation.
 *
 * Requirement 16.1: Display Move_History in a scrollable panel alongside the board
 * Requirement 16.5: Display moves in chronological order with move numbers
 */
export const MoveHistory: React.FC<CheckersMoveHistoryProps> = ({
  moves,
  currentMoveIndex,
  onMoveClick,
  className = '',
}) => {
  const historyRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest move
  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [moves.length]);

  /**
   * Handles clicking on a move to view that position
   */
  const handleMoveClick = (index: number) => {
    if (onMoveClick) {
      onMoveClick(index);
    }
  };

  /**
   * Groups moves into pairs (dark and light).
   * Requirement 16.5: chronological order with move numbers
   */
  const movePairs: Array<{ moveNumber: number; dark: CheckersMove; light?: CheckersMove }> = [];

  for (let i = 0; i < moves.length; i += 2) {
    movePairs.push({
      moveNumber: Math.floor(i / 2) + 1,
      dark: moves[i],
      light: moves[i + 1],
    });
  }

  return (
    <div className={`checkers-move-history ${className}`}>
      <div className="checkers-move-history-header">
        <h3>Move History</h3>
      </div>

      {/* Requirement 16.1: Scrollable move history */}
      <div className="checkers-move-history-content" ref={historyRef}>
        {moves.length === 0 ? (
          <div className="checkers-move-history-empty">No moves yet</div>
        ) : (
          <>
            {/* Column headers */}
            <div className="checkers-move-history-headers">
              <span className="checkers-header-number">#</span>
              <span className="checkers-header-dark">⚫ Dark</span>
              <span className="checkers-header-light">⚪ Light</span>
            </div>

            <div className="checkers-move-history-list">
              {movePairs.map((pair, pairIndex) => (
                <div key={pairIndex} className="checkers-move-pair">
                  <span className="checkers-move-number">{pair.moveNumber}.</span>

                  {/* Dark's move */}
                  <button
                    className={`checkers-move-button ${
                      currentMoveIndex === pairIndex * 2 ? 'active' : ''
                    }`}
                    onClick={() => handleMoveClick(pairIndex * 2)}
                    title={`View position after ${pair.dark.notation}`}
                  >
                    {/*
                     * Requirement 16.2: Board notation (e.g., "11-15")
                     * Requirement 16.3: Jump moves with "x" (e.g., "11x18")
                     * Requirement 16.4: Multi-jumps (e.g., "11x18x25")
                     */}
                    {pair.dark.notation}
                  </button>

                  {/* Light's move (if exists) */}
                  {pair.light && (
                    <button
                      className={`checkers-move-button ${
                        currentMoveIndex === pairIndex * 2 + 1 ? 'active' : ''
                      }`}
                      onClick={() => handleMoveClick(pairIndex * 2 + 1)}
                      title={`View position after ${pair.light.notation}`}
                    >
                      {pair.light.notation}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {moves.length > 0 && (
        <div className="checkers-move-history-footer">
          <span className="checkers-move-count">
            {moves.length} {moves.length === 1 ? 'move' : 'moves'}
          </span>
        </div>
      )}
    </div>
  );
};

export default MoveHistory;
